import { useState, useEffect } from 'react';
import { BookOpen, ChevronDown, ChevronUp, AlertTriangle, Compass, Library, Shield, Heart, Bell } from 'lucide-react';

const journey = [
  {
    id: 'signup',
    step: 1,
    preview: 'market',
    title: 'You sign up and get approved',
    where: 'Sign Up page',
    intro: 'Every Stokora account trades the US market — NYSE + NASDAQ, in USD. After signup, an admin approves your account and your 30-day free trial begins.',
    terms: [
      { t: 'Market', d: 'NYSE + NASDAQ. Live data from Polygon.io. All paper trading is in USD.' },
      { t: 'Pending → Approved', d: 'New accounts start in "pending" status. An admin reviews and approves the account; you get an email when access is granted.' },
      { t: 'Trial', d: 'A 30-day free trial starts the day your account is approved. After expiry, an admin can extend access (30 / 90 / 180 / 365 days or permanent). Your account and trade history are preserved either way.' },
    ],
  },
  {
    id: 'dashboard',
    step: 2,
    preview: 'chart',
    title: 'You open the Dashboard — the day\'s briefing',
    where: 'Dashboard page',
    intro: 'The Dashboard is the front door. It tells a one-screen story in three acts: where the market is today (indices, breadth, VIX), how the signal engine has been performing lately, and where YOU stand (cash, open positions, all-time P&L). A compact "Signal engine" tile teases today\'s top 3 picks; full signal cards live on the Signals tab so the home screen never duplicates them.',
    terms: [
      { t: 'Market Headline', d: 'A plain-English summary line at the top — e.g. "Bullish session — S&P 500 +0.85% · 62% advancing · VIX calm at 14.2." Derived from the same live data shown in the cards below, just narrated.' },
      { t: 'Index Chart', d: 'A 90-day line chart of the primary index — S&P 500, NASDAQ 100, Russell 2000, or Dow Jones. The current price is overlaid as a dashed line so you can place today in context.' },
      { t: 'Top Movers', d: 'The strongest gainers and losers of the current session. Useful for spotting unusual activity, but not a signal — a stock can lead the gainers list one day and be a poor trade the next.' },
      { t: 'Market Breadth', d: 'The percentage of all scanned stocks that are advancing today. Above ~65% = broadly bullish session (LONG trades have a tailwind). Below ~40% = broadly bearish (SHORT trades have a tailwind). Stokora factors this into every signal score.' },
      { t: 'Signal Track Record', d: 'Every day, the digest signals Stokora generates for your market are saved. Once each signal\'s swing window has elapsed, a background job replays the real price action against the original target and stop and records the outcome. The Dashboard widget shows the rolling 30-day win rate and average return across all scored signals. Stays blank with a "data appearing soon" note until at least one signal has been scored.' },
      { t: 'Your Portfolio tile', d: 'Total portfolio value, all-time P&L (with percentage), open-position count, and available cash. Click "Trade" to jump straight to the Trading page.' },
      { t: 'Signal engine tile', d: 'A compact summary of what the engine is seeing today — total count, LONG/SHORT split, average confidence — plus the top 3 picks as one-line entries (ticker · direction · confidence · target/stop). Click any row to open the security detail modal; click "All signals →" to jump to the full Signals page.' },
    ],
  },
  {
    id: 'signals',
    step: 3,
    preview: 'signal',
    title: 'You browse Trade Signals — the ideas of the day',
    where: 'Signals page',
    intro: 'This is the heart of the app. Stokora scans roughly 8,000–10,000 US stocks, filters by liquidity, scores the survivors across 13 independent indicator groups, and shows you the top 25. Each card is a hypothesis: "this stock is set up to move in this direction over the next few days, for these reasons."',
    terms: [
      { t: 'Direction — LONG / SHORT / HOLD', d: 'LONG = the setup favours the stock going up. SHORT = the setup favours the stock going down. HOLD = no clear edge in either direction.' },
      { t: 'Confidence Score (0–95%)', d: 'How many of the 13 indicators agreed with the direction and how extreme they are. More agreement = higher score. The "High Confidence" filter shows signals above the current page average.' },
      { t: 'VALIDATED by default', d: 'Every signal is automatically replayed against 90 days of real price history the moment it shows up — no button click needed. The card carries a green VALIDATED badge once the backtest finishes (typically a few seconds). If a ticker has too little history to backtest, the badge reads LIMITED DATA and the shown confidence is the original heuristic; if the backtest call itself errors, the badge reads HEURISTIC.' },
      { t: 'Target / Stop / R:R', d: 'Target = realistic profit price (~1.5× expected move). Stop = exit price if the setup fails (~1× expected move). R:R = reward-to-risk ratio. Both are derived from the stock\'s own volatility, not fixed percentages.' },
      { t: 'Swing (N days)', d: 'How long Stokora expects the setup to play out, computed per signal. Typically 3–5 days. Swing trades, not day trades.' },
      { t: 'Reason Tags', d: 'A few short phrases explaining why the signal fired — e.g. "RSI Oversold", "MACD Bullish Cross", "Above 50-day EMA". Click into a ticker for the full indicator breakdown.' },
      { t: 'News Sentiment Badge', d: 'Bullish / Bearish / Neutral, generated from recent company news. Only non-neutral sentiment is shown as a badge. US stocks only.' },
      { t: 'Options Play (US only)', d: 'A real options contract suggestion attached to each US signal: nearest at-the-money strike, 7–60 days to expiry, matching direction. REAL badge = live data from Polygon\'s options chain; EST badge = estimated when live data is unavailable.' },
      { t: 'Get AI Analysis', d: 'Sends all loaded signals to a large language model (Groq) in one bulk call. The AI returns a ±15 point adjustment with a short reasoning note per signal, plus a market-wide summary banner. Additive context — it cannot flip a weak signal into a strong one.' },
      { t: 'Backtest stats panel', d: 'Once a signal is validated, the card shows win rate, trial count, average return, profit factor, and Sharpe ratio — all from replaying the setup against 90 days of real daily price data.' },
    ],
  },
  {
    id: 'watchlist',
    step: 4,
    preview: 'heart',
    title: 'You build a Watchlist — your personal radar',
    where: 'Watchlist page',
    intro: 'The Watchlist is the tickers you care about — names you follow regardless of whether they showed up in today\'s top 25. Stokora evaluates each one with the same signal engine every time you load the page, so you always see a fresh read on your favourites.',
    terms: [
      { t: 'Live Signal Cards', d: 'Each watched ticker shows a current signal: direction, confidence, target, stop, RSI, reason tags, news sentiment. Loaded on demand from live data.' },
      { t: 'Paper Trade Button', d: 'One-click jump to the Trading page with the ticker, direction, and current price pre-filled. Options cards have a separate button for the suggested options contract.' },
      { t: 'AI Analysis', d: 'Same bulk Groq evaluation as the Signals page, but scoped to your watched tickers. Useful when your watchlist is your real focus list rather than the market-wide top 25.' },
    ],
  },
  {
    id: 'trading',
    step: 5,
    preview: 'pnl',
    title: 'You place a Paper Trade — learn without risking real money',
    where: 'Trading page',
    intro: 'Paper trading is the whole point. You take the signals you trust and act on them with mock money. Nothing is at risk; everything is recorded. Over weeks of trades you build a real, personal track record — which signals you actually win on, which you don\'t, where you size too big, where you exit too early.',
    terms: [
      { t: 'Starting Balance', d: 'Every account opens with $10,000 USD of paper-trading capital. Nothing is at risk; everything is recorded.' },
      { t: 'BUY vs SHORT', d: 'BUY profits when the price rises. SHORT profits when the price falls. For SHORT trades, capital is committed on open and released on close along with realised P&L.' },
      { t: 'Options (100× multiplier)', d: '1 options contract = 100 shares. Premium per share × 100 = total cost. Open options positions are valued using live bid/ask midpoint from Polygon, falling back to the last known price.' },
      { t: 'Open Positions vs History', d: 'Open positions are split into Stocks and Options tabs. The History tab shows every closed trade with realised P&L. Summary cards show total portfolio value, available cash, and total P&L vs your starting balance.' },
      { t: 'Signal Health — HOLD / MONITOR / CONSIDER EXIT', d: 'A live, real-time read of whether the original thesis still holds. Fresh technicals (RSI, MACD, Bollinger, Stochastic, SuperTrend, 50-day EMA) are scored against your trade direction. HOLD (green) = multiple indicators still agree. MONITOR (amber) = mixed signals, worth watching. CONSIDER EXIT (red) = at least two independent indicators have reversed. CONSIDER EXIT also fires a one-time email alert.' },
      { t: 'Close Position', d: 'Closes the trade at the live quote. For options, uses live bid/ask midpoint. Realised P&L is recorded and the portfolio P&L chart updates immediately.' },
      { t: 'Price Auto-Fill', d: 'Typing a ticker into the trade form fetches the current price after a short pause and fills it in. You can override it manually before submitting.' },
    ],
  },
  {
    id: 'detail',
    step: 6,
    preview: 'tabs',
    title: 'You dig into a ticker — the Security Detail view',
    where: 'Click any ticker card',
    intro: 'Every ticker — whether on the Dashboard, Signals page, Watchlist, or Trading page — opens a detail modal with the full picture: price history, technicals, options chain, and a probability distribution of likely outcomes. This is where you decide whether a signal is worth trading.',
    terms: [
      { t: 'Price Chart tab', d: 'The default view: a 90-day line chart of daily closes with volume bars below (green on up-days, red on down-days).' },
      { t: 'Today tab', d: 'The current session\'s 5-minute intraday bars. Switch to this tab on a trading day to see what the stock is doing right now.' },
      { t: 'Technicals Panel', d: 'Live RSI (14), MACD line / signal / histogram, Bollinger Upper / Mid / Lower Bands, BB Width, price vs mid band, and current volume — all computed locally from Polygon daily bars.' },
      { t: 'P&L Probability', d: 'A bell-curve probability distribution for 5-day returns, derived from the stock\'s implied volatility. Shows expected move, probability of profit, and the ±1σ price range (the band that contains ~68% of likely outcomes). Useful for sizing trades and setting realistic targets.' },
    ],
  },
  {
    id: 'alerts',
    step: 7,
    preview: 'bell',
    title: 'You let Stokora watch the market for you — Email Alerts',
    where: 'Runs in the background',
    intro: 'You don\'t have to log in every day. Stokora runs three alert jobs on your behalf and emails you only when something deserves your attention.',
    terms: [
      { t: 'Daily Digest', d: 'A morning email with the top 5 high-confidence signals for your default market (sent shortly after market open). Signals are scored fresh, filtered to ≥ 60% confidence, and quick-backtested before sending. If your watchlist has tickers, the digest is personalised to those names; otherwise it falls back to a curated high-liquidity universe.' },
      { t: 'Exit Alert', d: 'Every 15 minutes during market hours, Stokora checks the Signal Health of every open trade. If a position drops to CONSIDER EXIT (two independent indicators have reversed), you get a one-time email with entry price, current price, unrealised P&L, and the specific indicator reasons. The alert fires only once per trade — no spam if you choose to hold.' },
      { t: 'Subscription Expiry', d: 'A daily background job emails you 7 days and 1 day before your trial or paid subscription expires, plus a final notice the day access lapses. If an admin extends your access in between, the alert cycle re-arms automatically against the new expiry date. Account data and trade history are preserved even after access ends.' },
    ],
  },
];

const reference = [
  {
    id: 'indicators',
    title: 'Technical Indicators',
    intro: 'Stokora scores every signal across 13 independent indicator groups. No single one is enough — they have to agree. Here\'s what each one actually measures.',
    items: [
      { t: 'RSI — Relative Strength Index', d: 'A 0–100 scale of how stretched the recent move is. Above 70 = overbought (may pull back). Below 30 = oversold (may bounce). Tells you how extended a move already is, not which way it will go next.' },
      { t: 'MACD', d: 'Tracks two exponential moving averages (12-day vs 26-day). A bullish cross — MACD line crossing above the signal line — is what Stokora scores. The histogram flipping sign is often the earliest warning of a direction change.' },
      { t: 'Bollinger Bands', d: 'A volatility channel: 20-day SMA in the middle, ±2 standard deviations as the upper and lower bands. Price touching a band means the move is statistically stretched. A narrow band ("squeeze") often precedes a sharp move in either direction.' },
      { t: 'Stochastic Oscillator', d: 'Where today\'s close sits within the recent 14-day high/low range. Independent of RSI — it measures location, not speed. Both flagging oversold at the same time is a stronger signal than either alone.' },
      { t: 'SuperTrend', d: 'An ATR-based directional trend filter. Above the line = bullish trend. Below = bearish. With-trend signals historically have higher win rates than counter-trend ones.' },
      { t: '50-day EMA', d: 'The moving average most institutional desks watch. Price above the 50-day EMA = stock is in an accumulation regime; below = distribution. Aligning trades with this layer means trading with institutional flow.' },
      { t: 'ADX — Trend Strength', d: 'A 0–100 measure of how strong the trend is, with no direction information. Below 20 = choppy market (other indicators are unreliable). Above 25 = real trend; above 40 = strong trend.' },
      { t: 'OBV — On-Balance Volume', d: 'A running total of volume: added on up-days, subtracted on down-days. Tells you whether money is flowing in or out, which raw volume can\'t show. Divergence between OBV and price often precedes reversals.' },
      { t: 'VWAP (US only)', d: 'Volume-weighted average price — the benchmark institutional traders use. Closing above VWAP on heavy volume suggests institutional accumulation; below suggests distribution.' },
      { t: 'Market Breadth', d: 'Percentage of stocks advancing today. Computed from the full market scan. Used as a global modifier — a perfect chart against a broadly declining market still gets a lower score.' },
      { t: 'Relative Strength vs Index', d: 'How the stock moved today compared to the reference index (SPY for US). A stock up 4% on a flat market is showing real strength; the same stock up 4% on a +5% market is just riding the tide.' },
      { t: '52-Week High/Low Proximity', d: 'A stock within 5% of its 52-week high during a LONG signal is in a breakout zone — overhead resistance is clearing. Near a 52-week low during a SHORT signal is a confirmed breakdown.' },
      { t: 'Pivot Point S/R', d: 'Classic floor-trader pivots derived from yesterday\'s high/low/close. Price near S1 during a LONG = natural technical floor. Near R1 during a SHORT = natural ceiling.' },
      { t: 'Multi-Timeframe Confirmation', d: 'Stokora checks weekly RSI and weekly MACD alongside the daily readings. A daily signal agreed by the weekly is materially more reliable than a daily signal alone.' },
      { t: 'How they combine', d: 'Indicators must agree with the signal direction to earn points — contradicting readings score zero rather than being averaged in. The more that align, the higher the confidence. All indicators are lagging by nature; use them to assess probability, not certainty.' },
    ],
  },
  {
    id: 'options',
    title: 'Options & Greeks',
    intro: 'Every US signal carries an options suggestion alongside the underlying stock idea. Options are leveraged, time-decaying instruments — these terms tell you what you\'re actually buying.',
    items: [
      { t: 'Delta (Δ)', d: 'Option price change per $1 move in the stock. Calls: 0 to +1. Puts: 0 to −1. At-the-money options sit near ±0.5. Also a rough estimate of the probability the option expires in the money.' },
      { t: 'Gamma (Γ)', d: 'How fast Delta itself changes per $1 stock move. Highest near at-the-money and close to expiry. High Gamma means the position can flip from winner to loser quickly on a big move.' },
      { t: 'Theta (Θ)', d: 'Daily time decay in dollars. Always negative for buyers — you lose value every day you hold. Decay accelerates in the last two weeks before expiry.' },
      { t: 'Vega (V)', d: 'Option price change per 1% rise in implied volatility. Long options benefit from rising IV (good into earnings); short options are hurt.' },
      { t: 'Implied Volatility (IV)', d: 'The market\'s forecast of future movement. IV > 40% = expensive options (consider selling premium). IV < 20% = cheap options (consider buying). Stokora auto-selects the strategy based on IV.' },
      { t: 'Moneyness — ITM / ATM / OTM', d: 'ITM (In The Money) has intrinsic value. ATM means the strike is roughly the current price. OTM has only time value left.' },
      { t: 'Breakeven', d: 'For calls: strike + premium. For puts: strike − premium. The expiry price where the trade neither wins nor loses.' },
      { t: 'Strategy Guide', d: 'IV > 50% → sell premium (straddle, strangle, credit spread). IV 35–50% → debit spread. IV 20–35% → vertical spread. IV < 20% → buy outright calls or puts.' },
    ],
  },
  {
    id: 'data',
    title: 'Where the data comes from',
    intro: 'Stokora pulls from one real, paid market-data provider and one LLM provider. No proprietary or fabricated data anywhere on the platform.',
    items: [
      { t: 'Polygon.io', d: 'Full market scan (~8–10k tickers via the grouped-daily endpoint), live stock snapshots, 90-day daily OHLCV, the entire options chain with bid/ask, Greeks, IV, open interest, VIX index, company news, and the underlying bars used to compute every technical indicator locally on our server. One provider for everything.' },
      { t: 'Groq (AI layer)', d: 'A large-language-model evaluation layer (llama-3.3-70b-versatile) for the optional "Get AI Analysis" button. Bulk-called once per click; result is server-side cached for 30 minutes; entirely fail-safe — if Groq is down, mechanical signals still work normally.' },
    ],
  },
  {
    id: 'engine',
    title: 'V2 Signal Engine',
    intro: 'The scoring and risk system that drives every signal. Built around a single principle: decorrelated evidence beats stacked correlated indicators. Each piece below is a gate or a weight applied to every shipped signal.',
    items: [
      { t: 'Decorrelated cluster scoring', d: 'The 13 indicators above are grouped into 6 clusters — momentum, trend, volatility, volume, structure, macro. Each cluster contributes at most +2 to the raw score, so an oversold-momentum setup can\'t triple-count via RSI + Stoch + Weekly RSI. The "Setup: momentum" badge on each card shows the dominant cluster; "Score 8 · 5 clusters" tells you how broad the agreement is.' },
      { t: 'Score vs Confidence', d: 'Score is the raw cluster-summed evidence (max 12). Confidence is a 20–95 percentage that includes the score plus bonuses for data quality, RSI extremity, ADX trend strength, VWAP agreement, breadth, and 52-week proximity. Trust the calibrated confidence (green VALIDATED badge) more than the heuristic — it\'s weighted against the actual 90-day backtest.' },
      { t: 'VIX-aware regime gates', d: 'A three-band macro filter. VIX < 18 = calm (default thresholds). 18-25 = normal. VIX ≥ 25 = stressed (score floor lifts to 5, confidence to 65, counter-trend LONGs need score ≥ 10). A stressed-vol environment punishes weak setups hard, so the gate is automatically more selective.' },
      { t: 'LONG counter-trend filter', d: 'Below score 8 (10 in stressed VIX), a LONG signal must have stock-level uptrend confirmation: price > EMA50 AND (SuperTrend bullish OR weekly MACD bullish). Production data showed counter-trend LONGs were the dominant failure mode (25% WR vs 46% on SHORTs); this gate eliminates them while still allowing deep-oversold mean-reversion plays.' },
      { t: 'ATR-adaptive risk-reward', d: 'Target/stop ratios scale with the underlying\'s daily range. High-volatility names (ATR > 4%) use 1.7:1 — wider stops over-stretch the target. Calm names (ATR < 2.25%) use 2.2:1. Default 2:1 otherwise. A hard R:R floor of 1.7 means every shipped signal still clears positive expectancy at a 40% win rate.' },
      { t: 'Earnings blackout (−2 / +10 days)', d: 'Any candidate within 2 days after or 10 days before a scheduled earnings call is filtered out. Earnings gaps routinely override technical setups via overnight risk, so trades inside that window historically resolve as coin flips. Polygon\'s Benzinga calendar drives the gate.' },
      { t: 'Net-of-cost outcome tracking', d: 'Every persisted signal\'s eventual outcome is recorded after its swing window elapses, minus 15 bps round-trip slippage + commission (50 bps for illiquid sub-$10M-daily names). Dashboard numbers are net, not gross — matching what you would actually realise.' },
      { t: 'Wilson 95% confidence interval', d: 'The win-rate metric carries a binomial confidence band so a 40% read on n=15 reads as "25–58% true range" — preventing small samples from being misread as a reliable edge. The card shows a warning when scored count is < 30.' },
      { t: 'Multi-window track record (30 / 90 / 180 days)', d: 'The dashboard track-record card lets you switch between three rolling windows. A small 30-day sample at 40% WR might pair with a 180-day sample at 52% — the multi-window view shows the trajectory instead of a single snapshot.' },
      { t: 'Adaptive walk-forward weights', d: 'A nightly job aggregates net-of-cost outcomes per (cluster, regime, direction, window) and writes a weight per slot. Clusters with sub-50% WR get their contribution halved; sub-40% get floored at 0.3 (never zero — a cold streak can recover). Per-direction tracking means a poor-performing LONG-momentum combo gets dialled back without affecting SHORTs.' },
      { t: 'Direction quotas in the digest', d: 'When two signals tie on validated-status and confidence, the historically-better-performing direction wins the tiebreaker. Bias magnitude is small ((WR − 50) × 0.1 confidence points) so it breaks ties without overriding genuinely stronger counter-direction setups, and only activates with ≥10 scored samples per direction.' },
      { t: 'Option exit rules', d: 'Open option positions get three exit triggers layered on top of the underlying-signal check: premium drawdown ≤ −50% (stop-loss), premium gain ≥ +100% (take-profit), and < 5 trading days to expiry without thesis-confirmation gain (theta crunch). Any of the three flips the HOLD badge to EXIT regardless of what the chart is doing. With Polygon Options Developer, premium-based rules fire intraday on live bid/ask midpoint — not on yesterday\'s close.' },
      { t: 'Options liquidity filter', d: 'Every recommended contract must pass: open interest ≥ 10 (or any volume today), shares-per-contract = 100 (no OCC-adjusted contracts), and — when Polygon returns real-time bid/ask — bid/ask spread ≤ 30% of bid. The last gate makes sure the contract is actually two-sided tradeable, not just listed.' },
      { t: 'AI Bayesian prior', d: 'The AI confidence-adjustment swing (±15) shrinks toward ±6 over time as calibration data accumulates — only when observed hit-rate proves the model is miscalibrated. With no calibration data the prior is wide-open (full V1 swing). Means the AI overlay never silently caps itself before it\'s earned the right to.' },
      { t: 'Per-signal filter breakdown', d: 'The Signals page footer shows where every candidate went: "Scanned 8,124 → 50 top-momentum → 12 signals · 23 earnings blackout · 9 low confidence · 4 R:R · 2 exceptions". Lets you see immediately which gate is doing the work (or being too aggressive) on any given scan.' },
    ],
  },
];

const legal = [
  { t: 'Educational use only', d: 'Stokora is a paper-trading and learning platform. It is not a registered broker, financial advisor, or trading platform. All trades use mock money.' },
  { t: 'Not investment advice', d: 'Signals are algorithmic outputs. They are not investment recommendations. Past signal performance does not predict future results.' },
  { t: 'Data delays', d: 'Market data may be delayed up to 15 minutes depending on provider and session. Treat anything you see as a snapshot, not a real-time tick.' },
];

const DOCS_CSS = `
  @keyframes docs-toggle { 0%, 40% { left: 0; } 50%, 90% { left: 50%; } 100% { left: 0; } }
  @keyframes docs-draw { 0% { stroke-dashoffset: 600; } 50%, 100% { stroke-dashoffset: 0; } }
  @keyframes docs-bar { 0% { width: 0%; } 55%, 100% { width: 78%; } }
  @keyframes docs-badge-p { 0%, 45% { opacity: 1; } 55%, 100% { opacity: 0; } }
  @keyframes docs-badge-v { 0%, 45% { opacity: 0; transform: scale(0.8); } 55%, 100% { opacity: 1; transform: scale(1); } }
  @keyframes docs-badge-spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
  @keyframes docs-heart { 0%, 40% { opacity: 0; transform: scale(0.4); } 55% { opacity: 1; transform: scale(1.25); } 70%, 100% { opacity: 1; transform: scale(1); } }
  @keyframes docs-tab { 0%, 40% { left: 0; } 50%, 90% { left: 92px; } 100% { left: 0; } }
  @keyframes docs-bell { 0%, 65%, 100% { transform: rotate(0); } 72% { transform: rotate(-14deg); } 80% { transform: rotate(11deg); } 88% { transform: rotate(-6deg); } 94% { transform: rotate(3deg); } }
  .docs-prev { padding: 14px 12px; background: var(--bg-elevated, rgba(255,255,255,0.02)); border: 1px solid var(--border-subtle); border-radius: 8px; margin-bottom: 14px; display: flex; align-items: center; justify-content: center; min-height: 56px; }
  .docs-toggle-wrap { position: relative; display: inline-flex; padding: 4px; background: rgba(255,255,255,0.04); border-radius: 999px; gap: 0; }
  .docs-toggle-slide { position: absolute; top: 4px; bottom: 4px; left: 0; width: 50%; background: rgba(91,106,240,0.18); border: 1px solid rgba(91,106,240,0.35); border-radius: 999px; animation: docs-toggle 4s ease-in-out infinite; }
  .docs-toggle-chip { position: relative; z-index: 1; padding: 5px 14px; font-size: 11px; font-weight: 600; color: var(--text-primary); }
  .docs-chart-path { stroke-dasharray: 600; stroke-dashoffset: 600; animation: docs-draw 3.5s ease-in-out infinite; }
  .docs-bar-fill { height: 100%; background: linear-gradient(90deg, var(--accent), rgba(91,106,240,0.5)); border-radius: 999px; animation: docs-bar 3.5s ease-in-out infinite; }
  .docs-badge { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; font-size: 9px; font-weight: 700; letter-spacing: 0.5px; border-radius: 4px; }
  .docs-badge-p { background: rgba(91,106,240,0.18); color: #818cf8; animation: docs-badge-p 4s ease-in-out infinite; gap: 3px; }
  .docs-badge-v { background: rgba(34,197,94,0.18); color: var(--success, #22c55e); animation: docs-badge-v 4s ease-in-out infinite; }
  .docs-badge-spinner { display: inline-block; width: 7px; height: 7px; border-radius: 999px; border: 1.5px solid currentColor; border-top-color: transparent; animation: docs-badge-spin 0.8s linear infinite; }
  .docs-heart-fill { position: absolute; top: 0; left: 0; animation: docs-heart 3.5s ease-in-out infinite; transform-origin: center; }
  .docs-tab-row { position: relative; display: inline-flex; gap: 24px; padding-bottom: 6px; }
  .docs-tab-ind { position: absolute; bottom: 0; left: 0; width: 70px; height: 2px; background: var(--accent); border-radius: 1px; animation: docs-tab 4s ease-in-out infinite; }
  .docs-bell-icon { animation: docs-bell 2.6s ease-in-out infinite; transform-origin: top center; }
  .docs-pnl-num { font-variant-numeric: tabular-nums; font-size: 22px; font-weight: 700; color: var(--success, #22c55e); }
  .docs-journey-card { transition: transform 0.18s ease, border-color 0.18s ease; }
  .docs-journey-card:hover { transform: translateY(-1px); }
`;

function MarketPreview() {
  return (
    <div className="docs-prev">
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '6px 14px', borderRadius: 999,
        background: 'rgba(91,106,240,0.18)',
        border: '1px solid rgba(91,106,240,0.35)',
        fontSize: 11, fontWeight: 600, color: 'var(--text-primary)',
      }}>
        🇺🇸 NYSE + NASDAQ
      </div>
    </div>
  );
}

function ChartPreview() {
  return (
    <div className="docs-prev">
      <svg viewBox="0 0 200 50" width="220" height="44" preserveAspectRatio="none">
        <path
          d="M0 38 L20 34 L40 36 L60 22 L80 28 L100 16 L120 20 L140 10 L160 14 L180 6 L200 12"
          stroke="var(--accent)"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="docs-chart-path"
        />
      </svg>
    </div>
  );
}

function SignalPreview() {
  return (
    <div className="docs-prev">
      <div style={{ width: '100%', maxWidth: 280 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-primary)' }}>AAPL</span>
            <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 3, background: 'rgba(34,197,94,0.18)', color: 'var(--success, #22c55e)', fontWeight: 700 }}>LONG</span>
          </div>
          <div style={{ position: 'relative', width: 70, height: 16 }}>
            <span className="docs-badge docs-badge-p"><span className="docs-badge-spinner" />VALIDATING</span>
            <span className="docs-badge docs-badge-v">VALIDATED</span>
          </div>
        </div>
        <div style={{ height: 6, background: 'rgba(255,255,255,0.06)', borderRadius: 999, overflow: 'hidden' }}>
          <div className="docs-bar-fill" />
        </div>
        <div style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 4 }}>Confidence · scoring 13 indicators</div>
      </div>
    </div>
  );
}

function HeartPreview() {
  return (
    <div className="docs-prev">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: 220, padding: '6px 10px', background: 'rgba(255,255,255,0.03)', borderRadius: 6, border: '1px solid var(--border-subtle)' }}>
        <span style={{ fontSize: 11, color: 'var(--text-primary)', fontWeight: 600 }}>AAPL · Apple Inc.</span>
        <span style={{ position: 'relative', width: 16, height: 16, display: 'inline-block' }}>
          <Heart size={16} color="var(--text-muted)" strokeWidth={1.8} />
          <Heart size={16} color="var(--accent)" fill="var(--accent)" strokeWidth={1.8} className="docs-heart-fill" />
        </span>
      </div>
    </div>
  );
}

function PnLPreview() {
  const target = 247;
  const [val, setVal] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setVal(v => (v >= target ? 0 : Math.min(v + 11, target)));
    }, 45);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="docs-prev">
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 9, color: 'var(--text-muted)', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 2 }}>Realized P&amp;L</div>
        <div className="docs-pnl-num">+${val}</div>
      </div>
    </div>
  );
}

function TabsPreview() {
  return (
    <div className="docs-prev">
      <div className="docs-tab-row">
        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-primary)', width: 70, textAlign: 'center' }}>Price Chart</span>
        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-primary)', width: 70, textAlign: 'center' }}>Today</span>
        <span className="docs-tab-ind" />
      </div>
    </div>
  );
}

function BellPreview() {
  return (
    <div className="docs-prev">
      <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 18, background: 'rgba(91,106,240,0.12)' }}>
        <span className="docs-bell-icon" style={{ display: 'inline-flex' }}>
          <Bell size={18} color="var(--accent)" />
        </span>
      </div>
    </div>
  );
}

function Preview({ type }) {
  if (type === 'market') return <MarketPreview />;
  if (type === 'chart') return <ChartPreview />;
  if (type === 'signal') return <SignalPreview />;
  if (type === 'heart') return <HeartPreview />;
  if (type === 'pnl') return <PnLPreview />;
  if (type === 'tabs') return <TabsPreview />;
  if (type === 'bell') return <BellPreview />;
  return null;
}

function StepBadge({ n }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 26, height: 26, borderRadius: 'var(--r-pill)', flexShrink: 0,
      background: 'var(--grad-accent)', color: '#fff',
      fontSize: 12, fontWeight: 800, letterSpacing: '-0.3px',
      boxShadow: '0 2px 8px rgba(91,106,240,0.25)',
    }}>{n}</span>
  );
}

function JourneyCard({ s, open, toggle }) {
  return (
    <div
      className="card docs-journey-card"
      style={{
        borderColor: open ? 'var(--border-strong)' : 'var(--border-subtle)',
        overflow: 'hidden',
      }}
    >
      <button
        onClick={toggle}
        style={{
          padding: '14px 16px', cursor: 'pointer', width: '100%',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
          background: 'transparent', border: 'none', fontFamily: 'inherit', textAlign: 'left',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <StepBadge n={s.step} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.3 }}>{s.title}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>{s.where}</div>
          </div>
        </div>
        <ChevronDown
          size={18}
          color="var(--text-muted)"
          style={{
            flexShrink: 0,
            transition: 'transform var(--dur-base) var(--ease-out)',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </button>
      {open && (
        <div className="animate-fade-in" style={{ padding: '0 16px 18px 54px' }}>
          {s.preview && <Preview type={s.preview} />}
          <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.7, margin: '0 0 14px' }}>{s.intro}</p>
          <div style={{
            fontSize: 10, fontWeight: 700, color: 'var(--text-muted)',
            letterSpacing: '0.06em', textTransform: 'uppercase', margin: '8px 0 8px',
          }}>What the terms on this page mean</div>
          {s.terms.map((c, i) => (
            <div key={i} style={{ padding: '10px 0', borderTop: '1px solid var(--border-subtle)' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent-soft)', marginBottom: 4, letterSpacing: '-0.15px' }}>{c.t}</div>
              <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.65 }}>{c.d}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReferenceCard({ s, open, toggle }) {
  return (
    <div
      className="card"
      style={{
        borderColor: open ? 'var(--border-strong)' : 'var(--border-subtle)',
        overflow: 'hidden',
      }}
    >
      <button
        onClick={toggle}
        style={{
          padding: '14px 16px', cursor: 'pointer', width: '100%',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: 'transparent', border: 'none', fontFamily: 'inherit', textAlign: 'left',
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.15px' }}>{s.title}</span>
        <ChevronDown
          size={18}
          color="var(--text-muted)"
          style={{
            transition: 'transform var(--dur-base) var(--ease-out)',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </button>
      {open && (
        <div className="animate-fade-in" style={{ padding: '0 16px 16px' }}>
          {s.intro && <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.7, margin: '0 0 12px' }}>{s.intro}</p>}
          {s.items.map((c, i) => (
            <div key={i} style={{ padding: '10px 0', borderTop: '1px solid var(--border-subtle)' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent-soft)', marginBottom: 4, letterSpacing: '-0.15px' }}>{c.t}</div>
              <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.65 }}>{c.d}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GroupHeader({ icon: Icon, label, sub, tone = 'accent' }) {
  const tint =
    tone === 'amber'  ? 'rgba(245,158,11,0.18)' :
    tone === 'green'  ? 'rgba(34,197,94,0.18)'  :
    tone === 'purple' ? 'rgba(168,85,247,0.18)' :
                        'rgba(91,106,240,0.18)';
  const tintBorder =
    tone === 'amber'  ? 'rgba(245,158,11,0.30)' :
    tone === 'green'  ? 'rgba(34,197,94,0.30)'  :
    tone === 'purple' ? 'rgba(168,85,247,0.30)' :
                        'rgba(91,106,240,0.30)';
  const iconColor =
    tone === 'amber'  ? 'var(--amber)'       :
    tone === 'green'  ? 'var(--green)'       :
    tone === 'purple' ? '#c4b5fd'            :
                        'var(--accent-soft)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '28px 0 12px' }}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 30, height: 30, borderRadius: 'var(--r-md)',
        background: `linear-gradient(135deg, ${tint}, ${tint.replace('0.18','0.06')})`,
        border: `1px solid ${tintBorder}`,
        flexShrink: 0,
      }}>
        <Icon size={15} color={iconColor} strokeWidth={2.4} />
      </span>
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.1px' }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}

export default function DocsPage() {
  const [openId, setOpenId] = useState('signup');
  const toggle = (id) => setOpenId(openId === id ? null : id);

  return (
    <div className="animate-fade-in" style={{ maxWidth: 800, margin: '0 auto' }}>
      <style>{DOCS_CSS}</style>

      {/* Hero header — gradient icon tile to match the rest of the app */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 36, height: 36, borderRadius: 'var(--r-md)',
          background: 'linear-gradient(135deg, rgba(91,106,240,0.20), rgba(91,106,240,0.06))',
          border: '1px solid rgba(91,106,240,0.30)',
          flexShrink: 0,
        }}>
          <BookOpen size={19} color="var(--accent-soft)" strokeWidth={2.2} />
        </span>
        <h1 style={{ margin: 0 }}>Documentation</h1>
      </div>
      <p style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 20, marginLeft: 48 }}>
        Stokora v4.0 · NYSE + NASDAQ · Data: Polygon.io · AI: Groq
      </p>

      {/* Hero intro card */}
      <div className="card animate-fade-in" style={{
        padding: '18px 20px', position: 'relative', overflow: 'hidden', marginBottom: 16,
      }}>
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 2,
          background: 'var(--grad-accent)', opacity: 0.6,
        }} />
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 10, letterSpacing: '-0.1px' }}>
          What Stokora actually does
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7, margin: '0 0 10px' }}>
          The stock market is loud. Thousands of tickers, conflicting indicators, breaking headlines, opinions everywhere — and most retail traders end up reacting to noise rather than reading the chart.
        </p>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7, margin: 0 }}>
          Stokora cuts through that. Every day it scans ~8,000–10,000 US stocks, ranks the best setups across 13 independent indicator groups, validates them against 90 days of real price history, and presents the top 25 as actionable ideas. You then trade them with $10,000 of mock money — building a real, personal track record without risking a dollar. Think of it as a flight simulator for swing trading.
        </p>
      </div>

      {/* Compact warning strip */}
      <div className="animate-fade-in" style={{
        marginBottom: 18, padding: '10px 14px', borderRadius: 'var(--r-md)',
        background: 'var(--amber-dim)', border: '1px solid rgba(245,158,11,0.22)',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <AlertTriangle size={14} color="var(--amber)" style={{ flexShrink: 0 }} strokeWidth={2.2} />
        <span style={{ fontSize: 12, color: 'var(--amber-soft, var(--amber))', lineHeight: 1.5 }}>
          Market data may be delayed up to 15 min. Paper trades use mock money. Not financial advice.
        </span>
      </div>

      <GroupHeader
        icon={Compass}
        label="Your journey through Stokora"
        sub="Seven steps, from signup to your first review. Tap any step to expand."
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {journey.map(s => (
          <JourneyCard key={s.id} s={s} open={openId === s.id} toggle={() => toggle(s.id)} />
        ))}
      </div>

      <GroupHeader
        icon={Library}
        label="Reference"
        sub="Glossaries for the deeper terms — open when you need them."
        tone="purple"
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {reference.map(s => (
          <ReferenceCard key={s.id} s={s} open={openId === s.id} toggle={() => toggle(s.id)} />
        ))}
      </div>

      <GroupHeader
        icon={Shield}
        label="Account & legal"
        tone="amber"
      />
      <div className="card" style={{ padding: '4px 16px 12px' }}>
        {legal.map((c, i) => (
          <div key={i} style={{ padding: '12px 0', borderTop: i === 0 ? 'none' : '1px solid var(--border-subtle)' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent-soft)', marginBottom: 4, letterSpacing: '-0.15px' }}>{c.t}</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.65 }}>{c.d}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
